import {
	ContentSearchBar,
	CoveoConfigContextProvider,
	EnvironmentContextProvider,
	LayoutProvider,
	MainNavigation,
	ResourceContextImpl,
	ResourceContextProvider,
	SearchContextProvider,
	ThemeProvider,
	config,
	createCollateralLibraryRoutes,
	createLegalRoutes,
	createResourceRoutes,
	createSearchContext,
	createSearchRoute,
	useLayoutContext,
	type CoveoConfigContextValue,
	type CreateSearchContextParams,
	type LayoutNavComponentProps,
	type MarketingSite,
} from '@ds-mgnl/marketing';
import type { LinkSourceProps, LinkSourceRecord } from '@dsx/react';
import { Provider as DsxProvider } from '@dsx/react';
import { forwardRef } from 'react';
import {
	Outlet,
	Link as RouterLink,
	RouterProvider,
	createBrowserRouter,
} from 'react-router-dom';
import styles from './App.module.css';
import { TelsLogo, useNavData } from './components';
import { Footer } from './components/Footer';
import { useScrollManagement } from './hooks/useScrollManagement';
import { useUtmFormTracking } from './hooks/useUtmFormTracking';
import { ErrorPage } from './pages/ErrorPage';
import { MagnoliaPage } from './pages/MagnoliaPage';
import { magnoliaPageLoader } from './pages/magnoliaPageLoader';
import { getMagnoliaBaseDamUrl, getMagnoliaBaseUrl } from './util';

const linkSources = {
	default: forwardRef<HTMLAnchorElement, LinkSourceProps>((props, ref) => (
		<RouterLink ref={ref} {...props} to={props.href || ''} />
	)),
	external: forwardRef<HTMLAnchorElement, LinkSourceProps>((props, ref) => (
		<a ref={ref} {...props} />
	)),
	none: forwardRef<HTMLSpanElement, LinkSourceProps>((props, ref) => (
		<span ref={ref} {...props} />
	)),
} as LinkSourceRecord;

const SHOW_ON_SITE: MarketingSite = 'tels';
const CONTENT_SEARCH_HUB = 'TelsContentSearch';

const coveoConfig: CoveoConfigContextValue = {
	commerceTrackingId: process.env.COVEO_COMMERCE_TRACKING_ID ?? 'tels',
	commerceLanguage: process.env.COVEO_COMMERCE_LANGUAGE ?? 'en',
	commerceCountry: process.env.COVEO_COMMERCE_COUNTRY ?? 'US',
	commerceCurrency:
		process.env.COVEO_COMMERCE_CURRENCY ??
		('USD' as CreateSearchContextParams['commerceCurrency']),
	contentProxyBaseUrl: process.env.COVEO_CONTENT_PROXY_BASE_URL ?? '',
	commerceProxyBaseUrl: process.env.COVEO_COMMERCE_PROXY_BASE_URL ?? '',
	analyticsProxyBaseUrl: process.env.COVEO_ANALYTICS_PROXY_BASE_URL ?? '',
};

const searchContext = createSearchContext({
	...coveoConfig,
	commerceCurrency:
		coveoConfig.commerceCurrency as CreateSearchContextParams['commerceCurrency'],
	contentSearchHub: CONTENT_SEARCH_HUB,
});

function TelsNavigation({ transparent }: LayoutNavComponentProps) {
	const { ctaLinks, navData } = useNavData();
	return (
		<MainNavigation
			transparent={transparent}
			navData={navData}
			navLogo={TelsLogo}
			ctaLinks={ctaLinks}
			searchBar={
				<ContentSearchBar
					transparent={transparent}
					searchEngineConfig={{
						contentProxyBaseUrl: process.env.COVEO_CONTENT_PROXY_BASE_URL ?? '',
						searchHub: CONTENT_SEARCH_HUB,
					}}
				/>
			}
		/>
	);
}

const router = createBrowserRouter([
	{
		element: <RootLayout />,
		children: [
			...createSearchRoute({
				errorElement: <ErrorPage />,
				searchHomeUrlPath: '/search',
				tabs: ['content'],
			}),
			...createResourceRoutes({
				errorElement: <ErrorPage />,
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				resourcesHomeUrlPath: '/resources',
				pageConfig: config,
				showOnSite: SHOW_ON_SITE,
			}),
			...createLegalRoutes({
				errorElement: <ErrorPage />,
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				legalHomeUrlPath: '/legal',
				pageConfig: config,
				showOnSite: SHOW_ON_SITE,
			}),
			...createCollateralLibraryRoutes({
				errorElement: <ErrorPage />,
				collateralLibUrlPath: '/collateral-library',
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				mgnlBaseDamUrl: getMagnoliaBaseDamUrl() ?? '',
				mgnlCollateralHomePath: '/collateral-library',
				siteTitleSuffix: 'Direct Supply TELS',
				showOnSite: SHOW_ON_SITE,
				pageConfig: config,
			}),
			{
				path: '*',
				element: <MagnoliaPage />,
				loader: magnoliaPageLoader,
				errorElement: <ErrorPage />,
			},
		],
	},
]);

export function App() {
	useUtmFormTracking();
	const resourceContext = new ResourceContextImpl('/resources', SHOW_ON_SITE);

	return (
		<DsxProvider locale="en-US" linkSources={linkSources}>
			<ThemeProvider theme="tels">
				<CoveoConfigContextProvider value={coveoConfig}>
					<SearchContextProvider value={searchContext}>
						<ResourceContextProvider value={resourceContext}>
							<EnvironmentContextProvider
								value={{
									damHost: getMagnoliaBaseDamUrl() ?? '',
									appHost: getMagnoliaBaseUrl() ?? '',
									isInMagnolia: false,
								}}
							>
								<LayoutProvider initialNavigationComponent={TelsNavigation}>
									<RouterProvider router={router} />
								</LayoutProvider>
							</EnvironmentContextProvider>
						</ResourceContextProvider>
					</SearchContextProvider>
				</CoveoConfigContextProvider>
			</ThemeProvider>
		</DsxProvider>
	);
}

function RootLayout() {
	useScrollManagement();
	const { renderNavigation } = useLayoutContext();

	return (
		<div className={styles['site-container']}>
			{renderNavigation({
				transparencyOverlapRatio: 1,
				transparencyOverlapType: 'vertical',
			})}
			<main>
				<Outlet />
			</main>
			<Footer />
		</div>
	);
}
